import { FormDocument } from '@/features/ui/DocumentsData/model/types';

import {
  OwnerPayload,
  AttachmentPayload,
  toStringValue,
  buildOwner,
  mapAddress,
  AddressPayload,
} from '@/shared/lib/helpers/payloadHelpers';

import { ServiceFileManager } from '@/helpers/serviceFileManager';

import { UserRequestDraft, FormDataType } from '@/api/types';

import { collectAttachments, generateApplicationAttachment } from './payloadHelpers';

type GeneratePayloadParams = {
  requestId: UserRequestDraft['id'];
  formData: FormDataType;
  serviceFileManager?: ServiceFileManager;
};

interface Payload {
  requestId: UserRequestDraft['id'];
  subject_id: string;
  owner: OwnerPayload;
  is_addresses_match: boolean;
  registered_address: AddressPayload;
  declared_address: AddressPayload;
  org_npu: {
    id: Nullable<string>;
    name: Nullable<string>;
    isPayable?: boolean;
  };
  additional_documents: {
    additional_info?: Nullable<string>;
  };
  attachments: AttachmentPayload[];
}

export const generatePayload = async ({
  requestId,
  formData,
  serviceFileManager,
}: GeneratePayloadParams): Promise<{
  commonPayload: Payload;
  signedPayload: Payload & Partial<ReturnType<ServiceFileManager['getSignature']>>;
}> => {
  const passportDocument = (
    Array.isArray(formData.personPassportDocument) ? formData.personPassportDocument[0] : null
  ) as Nullable<FormDocument>;
  const hasActualAddress = (formData.isAddressesEqual as { id?: string })?.id === '0';
  const attachments = collectAttachments(formData, hasActualAddress);
  const applicationAttachment = await generateApplicationAttachment(serviceFileManager);

  const commonPayload: Payload = {
    requestId,
    subject_id: toStringValue(formData.subject_id) ?? '',
    owner: buildOwner(formData, passportDocument),
    is_addresses_match: !hasActualAddress,
    registered_address: mapAddress(formData.registeredAddress as Record<string, any>),
    declared_address: mapAddress(
      (hasActualAddress ? formData.actualAddress : formData.registeredAddress) as Record<string, any>,
    ),
    org_npu: {
      id: toStringValue((formData.org_npu as Record<string, unknown>)?.id),
      name: toStringValue((formData.org_npu as Record<string, unknown>)?.label),
    },
    additional_documents: {
      additional_info: toStringValue(formData.additional_info),
    },
    attachments,
  };

  const signedPayload = {
    ...commonPayload,
    attachments: applicationAttachment ? [...attachments, applicationAttachment] : attachments,
    ...(serviceFileManager?.getSignature() ?? {}),
  };

  return {
    commonPayload,
    signedPayload,
  };
};
